/* eslint-disable vue/one-component-per-file */
/* eslint-disable no-underscore-dangle */
var __decorate = this && this.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
    r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
    d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Vue, Component } from 'vue-property-decorator';
import * as immer from '@lyonbot/immer';
import { debounce, forEach, keyBy, set } from "lodash-es";
import uuidv4 from "../../lib/utils/uuidv4";
import { INJECT_UICORE_BUS, INJECT_UICORE_RENDERER } from "../../lib/utils/consts";
import { compileFlow } from '../simpleFlow/compileFlow';
import { patchVueInstance, vueBailDepCollector } from "../../lib/utils/vue";
import { VNodeFactory } from './VNodeFactory';
import { VNodeFactoryContext } from './VNodeFactoryContext';
import { DataStub, getPatchedDataSchema } from '@tencent/data-schema-core';
import { makeEffectStub } from "../../lib/utils/makeEffectStub";
import shallowEqual from "../../lib/utils/shallowEqual";
import { getCompPathOf } from './utils';
import { makePartialLayoutRendererComponent } from './PartialLayoutRenderer';
import { joinPath, splitPath } from '@tencent/data-schema-core/lib/utils/path';
import { createTriggersHost } from './TriggersHost';
import { getLayoutFromPagelet } from "../../lib/utils/layout";
import { Trie } from "../../lib/utils/collections";
import { parseCompSelector } from "../../lib/utils/compSelector";
import { isPatchedDataSchema } from '@tencent/data-schema-core/lib/getPatchedDataSchema';
import { StyleLoader } from './StyleLoader';
const CRendererBase = Vue.extend({
  inject: {
    ucParentRenderer: {
      from: INJECT_UICORE_RENDERER,
      default: null
    }
  },
  provide() {
    return {
      [INJECT_UICORE_RENDERER]: this,
      [INJECT_UICORE_BUS]: this.uc
    };
  },
  props: {
    /**
     * ucBus 上下文总线
     */
    uc: {
      type: Object,
      required: true
    },
    /**
     * 是否是根CRenderer，一般不用传值！！
     *
     * @remarks
     * 以前是直接叫做 `ucIsRoot`，但这个 prop 是不会变的，而 Vue 会带来大量无意义的监听，导致一些性能问题，
     * 所以 prop 里改成了 ucInternalIsRoot，然后在 created 的时候再去创建一个 非响应式的属性 `ucIsRoot`
     */
    ucInternalIsRoot: {
      type: Boolean,
      default: false
    },
    /**
     * 指定使用哪个 UICore.Pagelet
     */
    ucInitialPagelet: {
      type: Object,
      required: true
    },
    /**
     * 指定使用哪个初始 VNodeFactory，如果指定了，会跳过使用 UICore.Pagelet 初始化 VNodeFactory 的步骤，可用于加快卡片列表渲染
     */
    ucInitialVNodeFactory: {
      type: Object,
      default: null
    },
    /**
     * 要给这个页面片挂载入一些什么数据源
     *
     * 另见： addDataSource 方法
     */
    ucDataSourcesFromProps: {
      type: Array,
      default: () => []
    },
    /**
     * 是否需要 mixin 点什么东西？
     */
    ucVueOptions: {
      type: Object,
      default: null
    },
    /**
     * (作用域插槽用的) scopedSlotOwner 是谁？参数类型为 VNodeFactory
     */
    ucScopedSlotOwner: {
      type: Object,
      default: null
    },
    /** （作用域插槽用的） 这个插槽的名字 */
    ucScopedSlotId: {
      type: String,
      default: undefined
    },
    /** 产生作用域插槽里的vnode时 拉起这个回调 */
    onGeneratorVNode: {
      type: Function,
      default: null
    }
  }
});
let CRenderer = class CRenderer extends CRendererBase {
  constructor() {
    super(...arguments);
    /** 注意：这个可能是 null，不要依赖这里，请直接读取 ucPagelet  */
    this.ucInternalTempPagelet = null;
    this.ucInternalPageletRev = 0;
    this.ucMounted = false;
    this.ucStubCache = new Map();
    /** 当前页面片挂载的全部实例 */
    this.ucDataSources = {};
    /**
     * 描述整个页面片的 DataSchema
     */
    this.ucFullDataSchema = {
      type: 'object',
      fields: []
    };
    /** 页面变量，见 Pagelet?.stateSchema */
    this.state = {};
    /**
     * 渲染时发现有些组件有问题，这里会汇总出一个报告，key 是 compPath
     *
     * 仅限于设计模式，由 ucUpdateLayoutErrorList 维护内容
     */
    this.ucLayoutErrors = {};
  }
  get ucPagelet() {
    return this.ucInternalTempPagelet || this.ucInitialPagelet;
  }
  set ucPagelet(pagelet) {
    const p = pagelet ? Object.seal({
      ...pagelet
    }) : null;
    this.ucInternalTempPagelet = p;
  }
  get ucRendererUUID() {
    return uuidv4();
  }
  /** 上层页面，供子页面片引用的。外面注入的 ucParentRenderer 可能还是有点奇怪 */
  get parentScope() {
    if (!this.ucDepth) return undefined;
    return this.ucParentRenderer;
  }
  /**
   * 向当前页面片实例，添加或者更新一个 DataSourceDeclaration
   */
  addDataSourceDeclaration(info) {
    var _this$ucDataSources$i;
    const patchedSchema = isPatchedDataSchema(info.schema) && info.schema.id === info.id ? info.schema : getPatchedDataSchema({
      ...info.schema,
      id: info.id
    });
    const item = Object.seal({
      ...info,
      instance: (_this$ucDataSources$i = this.ucDataSources[info.id]) === null || _this$ucDataSources$i === void 0 ? void 0 : _this$ucDataSources$i.instance,
      schema: patchedSchema
    });
    this.$set(this.ucDataSources, info.id, item);
    this.ucRecomposeFullDataSchema();
  }
  /**
   * 挂载一个东西（比如，一个对象、一个 Vue 实例……）到当前页面片上，作为一个数据源提供
   *
   * 比如我有一个 userInfo 对象，想挂载到页面片里，
   * 让页面内组件和 LessCode 都能通过 `userInfo.xxx` 直接引用和访问里面的东西，
   * 那么，就可以使用这个方法把 userInfo 挂载上来。
   *
   * 注意：如果某个东西（this.xxx）早已经在当前页面片存在，那么请使用 `addDataSourceDeclaration` 方法。
   *
   * @param dataSource 描述这个数据源的信息，上面示例中的 `userInfo` 对象，就是里面的 instance
   */
  addDataSource(dataSource) {
    const {
      id,
      instance
    } = dataSource;
    this.addDataSourceDeclaration(dataSource);
    if (this.ucDataSources[id].instance !== instance) {
      this.ucDataSources[id].instance = instance;
    }
    Object.defineProperty(this, id, {
      enumerable: true,
      configurable: true,
      get: () => this.ucDataSources[id].instance
    });
  }
  ucRecomposeFullDataSchema() {
    const schema = {
      type: 'object',
      fields: []
    };
    forEach(this.ucDataSources, info => {
      schema.fields.push(info.schema);
    });
    this.uc.hooks.pageletDataSchemaAfterCompose.call(schema, this);
    this.ucFullDataSchema = Object.freeze(schema);
  }
  /** 将整个页面片作为 DataStub */
  get ucDataStub() {
    return new DataStub({
      schema: this.ucFullDataSchema,
      value: this
    });
  }
  /**
   * 如果你的组件里，希望可以输入一个 UICore.Layout 后直接渲染出来，
   * **并且复用当前页面片的上下文和各种变量**，
   * 那么可以使用这个工具
   *
   * 具体方式：在你的组件里：
   *
   * 1. 在 script 里 `inject: { renderer: { from: 'uicore:renderer' } }`
   * 2. 在 template 里 `<component is="renderer.ucPartialLayoutRenderer" :layout="你的那个布局JSON" />`
   */
  get ucPartialLayoutRenderer() {
    return makePartialLayoutRendererComponent(this);
  }
  beforeCreate() {
    const {
      uc,
      ucVueOptions,
      ucInitialPagelet
    } = this.$options.propsData; // 在 beforeCreate 阶段只能这样了
    if (ucVueOptions) patchVueInstance(this, ucVueOptions);
    uc.hooks.pageletRendererBeforeCreate.call(this, ucInitialPagelet);
  }
  beforeMount() {
    // 样式（依赖了数据源，所以放到这里）等待外部patchVueInstance created的部分全部执行完成
    this.ucStyleLoader.init();
    this.uc.rendererSet.add(this);
  }
  mounted() {
    this.ucMounted = true;
  }
  beforeDestroy() {
    this.ucMounted = false;
    this.ucUpdateLayoutErrorListDebounced.cancel();
    if (this.uc.renderer === this) this.uc.renderer = null;
    this.uc.rendererSet.delete(this);
  }
  created() {
    Object.defineProperty(this, 'uc', {
      value: this.uc,
      enumerable: true
    });
    // 初始化一些东西
    this.ucIsRoot = !!this.ucInternalIsRoot;
    this.ucDepth = this.ucIsRoot ? 0 : 1 + (this.ucParentRenderer ? this.ucParentRenderer.ucDepth : -1);
    this.ucRootRenderer = this.ucIsRoot ? this : this.ucParentRenderer.ucRootRenderer;
    // 引用——有的组件依赖了 uc.renderer 因此需要在这里提前挂载
    if (!this.uc.renderer && this.ucIsRoot) this.uc.renderer = this;
    // 一些奇怪的函数
    this.ucUpdateLayoutErrorListDebounced = debounce(() => this.ucUpdateLayoutErrorList(), 250);
    // 渲染用的上下文
    this.ucVNodeFactoryContext = Object.seal(new VNodeFactoryContext(this));
    this.$watch(() => this.ucInitialPagelet, () => {
      this.ucInternalTempPagelet = null;
      this.ucInternalPageletRev += 1;
    }, {
      immediate: true,
      deep: !!this.uc.isDesignMode
    });
    // 页面数据源发生变化，需要触发刷新
    this.$watch(() => this.ucDataStub, () => {
      this.$forceUpdate();
    });
    // -------------------------------------------------------
    let oldSourceIds = new Set();
    // eslint-disable-next-line function-paren-newline
    const syncDataSourcesFromProps = makeEffectStub(
    /**
     * 从 ucDataSourcesFromProps 把数据源同步过来的方法
     *
     * 同步过来后会主动添加 watch 监听深层和浅层的变化
     */
    ucDataSourcesFromProps => {
      const disposer = [];
      const newSourceIds = new Set();
      forEach(ucDataSourcesFromProps, (source, id) => {
        /* const alreadyExisting = */
        oldSourceIds.delete(id); // 删除 oldSourceIds 对应项，并检查是否已经存在
        newSourceIds.add(id);
        this.addDataSource(source);
        // 避免 props 外部修改，里面没同步的情况
        // Vue 随时里面的东西会变，真是可怕
        disposer.push(this.$watch(() => source.instance, () => this.ucDataSources[id].instance = source.instance), this.$watch(() => source.schema, () => this.addDataSourceDeclaration(source)));
      });
      // 此时 oldSourceIds 剩余的都是原来有但是现在被删除的
      // TODO: 开发删除数据源的专用方法？
      oldSourceIds.forEach(id => this.$delete(this.ucDataSources, id));
      oldSourceIds = newSourceIds;
      // 最后汇总一个大的 disposer
      return () => disposer.forEach(f => f());
    }, (x, y) => shallowEqual(x, y, 2));
    // 赶在ucDataSources被使用之前进行代理
    this.uc.hooks.pageletRendererCreatedMockStart.call(this);
    this.$watch(() => keyBy(this.ucDataSourcesFromProps, 'id'),
    // 浅层watch
    syncDataSourcesFromProps, {
      immediate: true
    });
    // 子页面片需要注入一个 parentScope
    if (this.ucDepth) {
      this.$watch(() => this.ucParentRenderer.ucFullDataSchema, schema => {
        this.addDataSourceDeclaration({
          id: 'parentScope',
          schema: {
            ...schema,
            id: 'parentScope',
            title: '当前区域外层',
            'uc:collapsed': true,
            'uc:bindable': false
          }
        });
      }, {
        immediate: true
      });
    }
    this.$once('hook:beforeDestroy', () => syncDataSourcesFromProps.cleanup());
    // ------------------------------------------------------
    // 数据源发生变化时，重置 ucStubCache 并强制重新绘制
    this.$watch(() => this.ucDataStub,
    // 隐含依赖里
    () => {
      this.ucStubCache = new Map();
      this.$forceUpdate();
    }, {
      immediate: true
    });
    // ------------------------------------------------------
    this.ucTriggerInit();
    this.ucStyleLoader = new StyleLoader(this);
    // 最后，调用插件的 hooks
    this.uc.hooks.pageletRendererCreated.call(this, this.ucPagelet);
  }
  /**
   * 在这里，你可以通过 componentId 去访问和修改组件的 props
   *
   * 例如： `compProps.xxxButton.disabled = true`
   */
  get compProps() {
    const {
      ucFactoryLUT
    } = this;
    return new Proxy({}, {
      get: (_, componentId) => {
        const factory = ucFactoryLUT.byId[componentId];
        if (!(factory !== null && factory !== void 0 && factory.comp)) return undefined;
        return new Proxy({}, {
          get: (_, propId) => {
            var _factory$comp$props$p, _factory$comp$props;
            return (_factory$comp$props$p = (_factory$comp$props = factory.comp.props) === null || _factory$comp$props === void 0 ? void 0 : _factory$comp$props[propId]) !== null && _factory$comp$props$p !== void 0 ? _factory$comp$props$p : factory.props[propId];
          },
          set: (_, propId, value) => {
            const newPagelet = immer.produce(this.ucPagelet, draft => {
              const layout = getLayoutFromPagelet(draft, factory.compPath);
              console.warn('[UICore] Temporarily set prop', factory.compPath, propId, value);
              if (layout) set(layout, ['props', propId], value);
            });
            this.ucPagelet = newPagelet;
            return true;
          }
        });
      },
      ownKeys: () => Object.keys(ucFactoryLUT.byId),
      isExtensible: () => false
    });
  }
  /**
   * 使用 keypath（例如 `"data.friends[0].name"`） 获取 data 的某个子 DataStub
   */
  getStubAtPath(path) {
    const pathString = Array.isArray(path) ? joinPath(path) : String(path);
    const caches = this.ucStubCache;
    let cached = caches.get(pathString);
    if (!cached) {
      vueBailDepCollector(() => {
        try {
          var _this$ucDataStub;
          const dataStub = (_this$ucDataStub = this.ucDataStub) === null || _this$ucDataStub === void 0 ? void 0 : _this$ucDataStub.getStubAtPath(path);
          cached = dataStub;
        } catch (e) {
          var _e$message;
          if ((_e$message = e.message) !== null && _e$message !== void 0 && _e$message.includes('Unknown remainder')) {
            console.error(`Error path in ${path}`);
          } else {
            throw e;
          }
        }
      });
      if (cached) caches.set(pathString, cached);
    }
    return cached;
  }
  /**
   * 根据 componentId 或者 compPath 获取组件实例
   */
  getRef(componentIdOrCompPath) {
    var _this$$refs$component;
    return (_this$$refs$component = this.$refs[componentIdOrCompPath]) !== null && _this$$refs$component !== void 0 ? _this$$refs$component : this.getRefByPath(componentIdOrCompPath);
  }
  /**
   * 解析一个 selector 找到组件及其所属的 renderer（可能会跑到外层去）
   *
   * 如果找到，返回 ` { renderer, compPath }`，否则返回 null。
   */
  ucQuerySelector(selector, base) {
    return parseCompSelector(this, base || '', selector);
  }
  get ucFactoryLUT() {
    var _this$ucTriggersHost;
    const byId = {};
    const byCompPath = {};
    const byBindingDataPath = new Trie();
    const walkPending = [this.ucVNodeFactory];
    const factoriesOfTriggers = (_this$ucTriggersHost = this.ucTriggersHost) === null || _this$ucTriggersHost === void 0 ? void 0 : _this$ucTriggersHost.triggerFactories;
    if (factoriesOfTriggers) walkPending.push(...factoriesOfTriggers);
    while (walkPending.length) {
      var _item$children;
      const item = walkPending.shift();
      if (!item) continue;
      const {
        comp
      } = item;
      if (typeof item.compPath === 'string') byCompPath[item.compPath] = item;
      if (comp.componentId) byId[comp.componentId] = item;
      if (comp.bindings) forEach(comp.bindings, dataPath => {
        try {
          const path = splitPath(dataPath);
          byBindingDataPath.add(path, item);
        } catch (e) {
          console.error(`[UICore] 组件:${item.compPath} 不合法的绑定路径: ${dataPath}`, e);
        }
      });
      if ((_item$children = item.children) !== null && _item$children !== void 0 && _item$children.length) walkPending.push(...item.children);
    }
    return Object.freeze({
      byId,
      byCompPath,
      byBindingDataPath
    });
  }
  /**
   * 根据 path 获取组件实例
   *
   * @param {string} path 例如 "/default/0/default/5"
   */
  getRefByPath(path) {
    // 这只是加个 Vue 依赖，保证 getRefByPath 为 computed 的时候是响应式的
    // eslint-disable-next-line
    this.ucVNodeFactory;
    return this.ucVNodeFactoryContext.componentInstances[path];
  }
  /**
   * （临时地）获取某个组件的 prop，这个不一定稳定，下次渲染或者 pagelet 变了就会丢失
   */
  getProp(componentId, propName) {
    var _this$compProps$compo;
    return (_this$compProps$compo = this.compProps[componentId]) === null || _this$compProps$compo === void 0 ? void 0 : _this$compProps$compo[propName];
  }
  /**
   * （临时地）修改某个组件的 prop，这个不一定稳定，下次 pagelet 变了就会丢失
   */
  setProp(componentId, propName, propValue) {
    const dst = this.compProps[componentId];
    if (dst) dst[propName] = propValue;
  }
  get ucBasicSimpleFlowCtxBase() {
    return {
      atoms: this.uc.flowAtoms,
      renderer: this,
      uc: this.uc,
      interopContext: this
    };
  }
  /**
   * 准备 compileFlow（生成流程执行函数） & 流程面板自动补全 需要的信息
   *
   * @param target 可选，指定发起这个事件的 Vue 实例是谁（必须是当前页面片的组件）？可以传入是一个 compPath，一个组件ID，或者一个组件实例
   */
  getSimpleFlowCtxBaseOf(target) {
    // eslint-disable-next-line eqeqeq
    const targetCompPath = target == null ? null : getCompPathOf(this, target);
    if (typeof targetCompPath !== 'string') return this.ucBasicSimpleFlowCtxBase;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return {
      ...this.ucBasicSimpleFlowCtxBase,
      compPath: targetCompPath,
      get target() {
        // 在执行时动态获取
        return self.getRefByPath(targetCompPath);
      }
    };
  }
  /**
   * 执行一个简单流程
   *
   * 注意：如果流程被提前终止，或者流程出错，都会 throw 一个 error。
   *
   * - 要判断是否为提前终止，可判断 error.isFlowAbortError
   * - (对于高级玩家) 如果要获取 SimpleFlow 的具体情况，可访问 error.flowCtx
   */
  async executeSimpleFlow(steps, opts = {}) {
    const executor = compileFlow({
      steps
    }, this.getSimpleFlowCtxBaseOf(opts.target));
    return await executor({
      arguments: opts.args,
      state: opts.state
    });
  }
  /** 页面根节点的 VNodeFactory */
  get ucVNodeFactory() {
    var _this$ucPagelet;
    if (!this.ucInternalTempPagelet && this.ucInitialVNodeFactory) return this.ucInitialVNodeFactory;
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.ucInternalPageletRev; // dep
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.uc.components; // dep
    const layout = (_this$ucPagelet = this.ucPagelet) === null || _this$ucPagelet === void 0 ? void 0 : _this$ucPagelet.layout;
    return vueBailDepCollector(() => {
      if (!layout) return null;
      return Object.freeze(new VNodeFactory({
        uc: this.uc,
        comp: layout,
        parent: this.ucScopedSlotOwner,
        isScopeRoot: true,
        scopedSlotId: this.ucScopedSlotId
      }));
    });
  }
  /**
   * 获取一个组件的作用域插槽实例，返回 CRenderer[] 列表
   *
   * @param component 哪一个组件，应该传入组件的ID或者组件引用
   * @param slotId 插槽名
   * @returns
   */
  ucGetScopedSlotInstances(component, slotId) {
    var _this$ucVNodeFactoryC;
    const compPath = getCompPathOf(this, component);
    if (typeof compPath !== 'string') return [];
    return ((_this$ucVNodeFactoryC = this.ucVNodeFactoryContext.factoryStates[compPath]) === null || _this$ucVNodeFactoryC === void 0 ? void 0 : _this$ucVNodeFactoryC.getScopedSlotInstances(slotId)) || [];
  }
  /**
   * (设计模式专用) 更新 ucLayoutErrors 信息
   */
  ucUpdateLayoutErrorList() {
    const errors = {};
    forEach(this.ucVNodeFactoryContext.factoryStates, (state, compPath) => {
      const allLayoutErrors = state.layoutErrors;
      if (allLayoutErrors && allLayoutErrors.length) {
        errors[compPath] = allLayoutErrors;
      }
    });
    // 如果前后都没啥错误，就不用更新，可以减少一次 Vue 重建
    if (Object.keys(errors).length === 0 && Object.keys(this.ucLayoutErrors).length === 0) return;
    this.ucLayoutErrors = errors;
  }
  ucTriggerInit() {
    this.ucTriggersHost = createTriggersHost(this);
  }
  // #endregion
  /** 暴露在实例上 提供给xy-client进行mock数据收集 */
  collectCRendererRuntimeData() {
    return this.uc.hooks.pageletRendererCollectRuntimeData.call(null, this);
  }
  render() {
    var _this$ucVNodeFactory;
    this.ucVNodeFactoryContext.depend();
    const vnode = ((_this$ucVNodeFactory = this.ucVNodeFactory) === null || _this$ucVNodeFactory === void 0 ? void 0 : _this$ucVNodeFactory.toVNode(this.ucVNodeFactoryContext)) || null;
    vueBailDepCollector(() => {
      var _this$onGeneratorVNod;
      (_this$onGeneratorVNod = this.onGeneratorVNode) === null || _this$onGeneratorVNod === void 0 || _this$onGeneratorVNod.call(this, vnode);
    });
    if (this.uc.isDesignMode) {
      this.ucUpdateLayoutErrorListDebounced();
    }
    return vnode;
  }
};
CRenderer = __decorate([Component({
  name: 'UicoreCRenderer'
})], CRenderer);
export { CRenderer };
export default CRenderer;
//# sourceMappingURL=CRenderer.js.map
